import coreAPI from 'apis/core';
import { axiosPrivate } from 'app/utils';

// function getCustomInfoFlag(service) {
//   return (
//     (service.service_catalog_name && service.service_catalog_name !== service.service_name) ||
//     (service.service_catalog_description &&
//       service.service_catalog_description !== service.service_description)
//   );
// }

export const getPharmacy = async ({ pharmacyId }) => {
  const response = await coreAPI.pharmacy.getPharmacy({ pharmacyId });

  return response.data[0];
};

export const getUserPharmacies = async ({ userId }) => {
  const { pharmacies } = await coreAPI.pharmacy.getUserPharmacies({ userId });

  return pharmacies;
};

export const getPharmacyStats = async ({ pharmacyId }) => {
  const { stats } = await coreAPI.pharmacy.getPharmacyStats({ pharmacyId });

  return stats;
};

export const setPatientThreadPin = async ({ pharmacyId, patientId, isPinned }) => {
  await coreAPI.pharmacy.togglePatientThreadPin({ pharmacyId, patientId, isPinned });
};

export const getPharmacyPendingAppointmentsCount = ({ pharmacyId }) => {
  return coreAPI.pharmacy.getAppointmentsCountByStatus({ pharmacyId, status: 'booked' });
};

export const getPatientMessagesThread = ({ pharmacyId, patientId, limit = 25 }) => {
  return coreAPI.pharmacy.getPharmacyPatientMessages({ pharmacyId, patientId, limit });
};

export const getPatientMessageThreadV3 = ({
  pharmacyId,
  patientId,
  limit = 25,
  orderBy = 'desc',
}) => {
  return coreAPI.pharmacy.getPharmacyPatientMessagesV3({ pharmacyId, patientId, limit, orderBy });
};

export const sendMessageToPatient = ({ pharmacyId, patientId, message }) => {
  const date = Math.floor(Date.now() / 1000);
  return coreAPI.pharmacy.postMessage({ pharmacyId, patientId, message, date });
};

export const sendMessageToPatientV3 = ({ pharmacyId, patientId, messageBody }) => {
  return coreAPI.pharmacy.postMessageV3({ pharmacyId, patientId, messageBody });
};

export const getPredefinedMessages = ({ pharmacyId }) => {
  return coreAPI.pharmacy.getPredefinedMessages({ pharmacyId });
};

export const getPharmacyInviteLink = async ({ pharmacyId }) => {
  const data = await coreAPI.pharmacy.getPharmacyInviteLink({ pharmacyId });
  return data.short_url;
};

export const uploadDmrPharmacyInviteCsv = ({ pharmacyId, csv }) => {
  const formData = new FormData();
  formData.append('csv', csv);

  return coreAPI.pharmacy.uploadDmrPharmacyInviteCsv({ pharmacyId, formData });
};

export const invitePatient = ({ pharmacyId, formData }) => {
  return coreAPI.pharmacy.invitePatient({ pharmacyId, formData });
};

export const getPharmacyMembers = ({ pharmacyId }) => {
  return coreAPI.pharmacy.getPharmacyMembers({ pharmacyId });
};

export const invitePharmacist = ({ pharmacyId, formData }) => {
  return coreAPI.pharmacy.invitePharmacist({ pharmacyId, formData });
};

export const removePharmacist = ({ pharmacyId, pharmacistId }) => {
  return coreAPI.pharmacy.removePharmacist({ pharmacyId, pharmacistId });
};

export const getPatientNotes = async (params) => {
  const response = await coreAPI.pharmacy.getPatientNotes(params);

  const note = response.data?.[0] || null;
  return note;
};

export const updatePatientNote = async (params) => {
  if (!params.pharmacyId || !params.patientId) throw new Error('Invalid request');

  const updatedNote = params.noteId
    ? await coreAPI.pharmacy.updateNote(params)
    : await coreAPI.pharmacy.createNote(params);

  return updatedNote;
};

export const updatePharmacyDeliveryOptions = async ({ pharmacyId, deliveryOptions }) => {
  if (!deliveryOptions || !pharmacyId) throw new Error('Invalid request');

  const updatedPharmacy = await coreAPI.pharmacy.updatePharmacyDeliveryOptions({
    pharmacyId,
    deliveryOptions,
  });
  return updatedPharmacy;
};

export const getPharmacyServices = async ({ pharmacyId }) => {
  if (!pharmacyId) throw new Error('Invalid request');

  const response = await coreAPI.pharmacy.getPharmacyServices({ pharmacyId });
  return response.data;
};

export const getPharmacyService = async ({ pharmacyId, serviceId }) => {
  if (!pharmacyId || !serviceId) throw new Error('Invalid request');

  const response = await coreAPI.pharmacy.getPharmacyService({ pharmacyId, serviceId });
  const service = response.data[0];
  if (service.schedules?.some((schedule) => !schedule.id || !schedule.range)) {
    service.schedules = service.schedules
      .sort((schedule1, schedule2) => {
        if (schedule1.default) return -1;
        if (schedule2.default) return 1;
        return schedule1.start_time - schedule2.start_time;
      })
      .map((schedule, index) => {
        schedule.id = index + 1;
        if (!schedule.range) schedule.range = 'opening_hours';
        return schedule;
      });
  }

  return service;
};

export const deletePharmacyService = async ({ pharmacyId, serviceId }) => {
  if (!pharmacyId || !serviceId) throw new Error('Invalid request');

  const response = await coreAPI.pharmacy.deletePharmacyService({ pharmacyId, serviceId });
  return response.data[0];
};

export const updateServiceSchedule = async ({ serviceId, schedules }) => {
  if (!serviceId || !schedules) throw new Error('Invalid request');

  const remappedSchedules = schedules.map((schedule) => {
    const { start_time: startTime, end_time: endTime, ...rest } = schedule;
    return {
      ...rest,
      ...(startTime !== undefined && { startTime }),
      ...(endTime !== undefined && { endTime }),
    };
  });

  const response = await axiosPrivate.put(`/v3/services/${serviceId}/schedules`, {
    schedules: remappedSchedules,
  });

  const service = response.data[0];
  return service;
};

export const getPharmacyConsultationRooms = async ({ pharmacyId }) => {
  if (!pharmacyId) throw new Error('Invalid request');

  const response = await coreAPI.pharmacy.getConsultationRooms({ pharmacyId });
  return response.data;
};

export const createPharmacyConsultationRoom = async ({ pharmacyId, payload }) => {
  if (!pharmacyId || !payload.name) throw new Error('Invalid request');

  const response = await coreAPI.pharmacy.addConsultationRoom({ pharmacyId, payload });
  return response.data[0];
};

export const updatePharmacyConsultationRoom = async ({
  pharmacyId,
  consultationRoomId,
  payload,
}) => {
  if (!pharmacyId || !consultationRoomId || !payload.name) throw new Error('Invalid request');

  const response = await coreAPI.pharmacy.updateConsultationRoom({
    pharmacyId,
    consultationRoomId,
    payload,
  });
  return response.data[0];
};

export const deletePharmacyConsultationRoom = async ({ pharmacyId, consultationRoomId }) => {
  if (!pharmacyId || !consultationRoomId) throw new Error('Invalid request');

  const response = await coreAPI.pharmacy.deleteConsultationRoom({
    pharmacyId,
    consultationRoomId,
  });
  return response.data[0];
};

export const getPharmacyInactivePatients = async ({ pharmacyId }) => {
  if (!pharmacyId) throw new Error('Invalid request');

  const response = await coreAPI.pharmacy.getInactivePatients({ pharmacyId });
  return response.data;
};

export const getPharmacyUnusedInvites = async ({ pharmacyId, pagination }) => {
  if (!pharmacyId) throw new Error('Invalid request');

  const response = await coreAPI.pharmacy.getUnusedInvites(pharmacyId, pagination);
  return { invites: response.data, pagination: response.pagination };
};

export const getPharmacyUnassignedDMRPatients = async ({ pharmacyId }) => {
  if (!pharmacyId) throw new Error('Invalid request');

  const response = await coreAPI.pharmacy.getUnassignedPatients({ pharmacyId });
  return response.data;
};

export const updatePatientInvite = async ({ pharmacyId, inviteId, payload }) => {
  if (!pharmacyId || !inviteId) throw new Error('Invalid request');
  if (Object.keys(payload).length === 0) throw new Error('Invalid request: no updates provided');

  const response = await coreAPI.pharmacy.updatePatientInvite(pharmacyId, inviteId, payload);
  return response.data[0];
};

export const getPharmacyPatient = async ({ pharmacyId, patientId, dependents }) => {
  if (!pharmacyId) throw new Error('Invalid request: Pharmacy required');
  if (!patientId) throw new Error('Invalid request: Patient required');

  const response = await coreAPI.pharmacy.getPharmacyPatient({ pharmacyId, patientId, dependents });
  return response.data[0];
};

export const updatePharmacyPatient = async ({ pharmacyId, patientId, dependentId, payload }) => {
  if (!pharmacyId) throw new Error('Invalid request: Pharmacy required');
  if (!patientId && !dependentId) {
    throw new Error('Invalid request: Patient or dependent is required');
  }
  if (!payload) throw new Error('Invalid request: payload required');

  const response = await coreAPI.pharmacy.updatePharmacyPatient({
    pharmacyId,
    patientId,
    dependentId,
    payload,
  });

  return response.data[0];
};

export const checkPharmacyPatient = async ({ pharmacyId, email, patientType }) => {
  if (!pharmacyId) throw new Error('Invalid request: Pharmacy required');
  if (!email) throw new Error('Invalid request: Email required');
  if (!patientType) throw new Error('Invalid request: Patient type required');

  const response = await coreAPI.pharmacy.checkPatient({ pharmacyId, email, patientType });
  return response.data[0];
};

export const getPatientOrderSchedules = async ({ pharmacyId, patientId, dependents }) => {
  if (!pharmacyId) throw new Error('Invalid request: Pharmacy required');
  if (!patientId) throw new Error('Invalid request: Patient required');

  const response = await coreAPI.pharmacy.getPatientOrderSchedules({
    pharmacyId,
    patientId,
    dependents,
  });
  return response.data;
};

export const upsertPharmacyPatient = async ({ pharmacyId, patientId, payload }) => {
  if (!pharmacyId) throw new Error('Invalid request. PharmacyId is required');
  if (Object.keys(payload).length === 0) throw new Error('Invalid request. No payload provided');

  const response = patientId
    ? await coreAPI.pharmacy.updatePatient(pharmacyId, patientId, payload)
    : await coreAPI.pharmacy.createPatient(pharmacyId, payload);

  return response.data[0];
};

export const getPharmacyLinkRequests = async ({ page = 0, limit = 10, orderBy, pharmacyId }) => {
  const response = await coreAPI.pharmacy.getPharmacyLinkRequests({
    pharmacyId,
    page,
    limit,
    orderBy,
  });
  const { requests, pagination } = response;

  return {
    nextPage: pagination.next,
    previousPage: pagination.previous,
    currentPageNumber: pagination.current_page,
    requestsTotal: pagination.total,
    requests,
  };
};

export const getPharmacyLinkRequestById = async ({ pharmacyId, requestId }) => {
  const response = await coreAPI.pharmacy.getPharmacyLinkRequestById({ pharmacyId, requestId });
  return response.linkRequest;
};

export const updatePharmacyLinkRequestStatus = async ({ pharmacyId, requestId, status }) => {
  const response = await coreAPI.pharmacy.updatePharmacyLinkRequestStatus({
    pharmacyId,
    requestId,
    status,
  });

  return response;
};

export const getPharmacyPhotoOrders = async ({ page = 0, limit = 10, pharmacyId, userId }) => {
  const response = await coreAPI.pharmacy.getPharmacyPhotoOrders({
    pharmacyId,
    userId,
    page,
    limit,
  });
  const { requests } = response;

  return {
    requests,
  };
};

export const getPharmacyPatientHelpStats = async ({ pharmacyId }) => {
  return coreAPI.pharmacy.getPatientHelpStats({ pharmacyId });
};

export const saveServiceContent = async ({
  pharmacyId,
  serviceId,
  content,
  shortDescription,
  contentJson,
}) => {
  if (!pharmacyId || !serviceId || !content) throw new Error('Invalid request');

  const response = await coreAPI.pharmacy.saveServiceContent({
    pharmacyId,
    serviceId,
    content,
    shortDescription,
    contentJson,
  });

  return response.data[0];
};

export {
  searchDMRPatients,
  getInactiveDMRPatients,
  getPatientMedicines,
  addPatientMedicine,
  deletePatientMedicine,
  createDigitalManagedRepeat,
  updateDigitalManagedRepeat,
  getDMRStats,
  getActiveDMRStats,
  getActiveDMRs,
  findActiveDMRsByPatient,
  getSingleDMR,
  deleteDMR,
  bulkApproveDMRs,
} from './managed-repeats';

export {
  getDeliverySubscriptions,
  createDeliveryExemption,
  createDeliverySubscription,
} from './delivery-subscriptions';

export default {};
